import { createResource, createSignal, For, Show } from "solid-js";
import type {
  AutomationDecision,
  AutomationInput,
  AutomationRule,
  SwitchTarget,
} from "@energyplatform/client";
import { api } from "../services.ts";
import { locale, t } from "../i18n.ts";
import { formatDateTime, formatNumber } from "../format.ts";
import { pathTo, siteTree } from "../context.ts";
import { treeRows } from "../tree.ts";
import { Icon } from "../icons.tsx";
import { ErrorBox } from "../components/ErrorBox.tsx";
import {
  ConfirmDialog,
  Dialog,
  operator,
  PageHeader,
  Skeleton,
  useAction,
} from "../components/ui.tsx";

const KINDS = [
  "schedule",
  "threshold",
  "pvSurplus",
  "price",
  "pvForecast",
] as const;
type Kind = (typeof KINDS)[number];
const DAYS = [1, 2, 3, 4, 5, 6, 7] as const;

const placeName = (id: string) =>
  pathTo(siteTree()?.nodes ?? [], id)
    .map((n) => n.name)
    .join(" / ") || id;

const num = (v: number, digits = 1) => formatNumber(v, locale(), digits);

/** Why a rule decided what it did, in words, from its reason code and the values behind it. */
function reasonText(
  d: AutomationDecision,
  rule: AutomationRule | undefined,
): string {
  const v = d.values;
  switch (d.reason) {
    case "scheduleOn":
    case "scheduleOff":
      return t(`automations.reason.${d.reason}`, {
        on: rule?.schedule?.on ?? "",
        off: rule?.schedule?.off ?? "",
      });
    case "thresholdHeld":
    case "thresholdCleared":
      return t(`automations.reason.${d.reason}`, {
        value: num(v["value"] ?? 0, 2),
        level: num(v["level"] ?? 0, 2),
        minutes: v["minutes"] ?? 0,
        comparison: t(
          `automations.comparison.${rule?.threshold?.comparison ?? "above"}`,
        ),
      });
    case "surplusExport":
      return t("automations.reason.surplusExport", {
        kw: num(-(v["gridKw"] ?? 0), 2),
        level: num(v["exportKw"] ?? 0, 1),
        minutes: v["minutes"] ?? 0,
      });
    case "surplusImport":
      return t("automations.reason.surplusImport", {
        kw: num(v["gridKw"] ?? 0, 2),
        level: num(v["importKw"] ?? 0, 1),
        minutes: v["minutes"] ?? 0,
      });
    case "priceHeld":
    case "priceCleared":
      return t(`automations.reason.${d.reason}`, {
        price: num(v["price"] ?? 0, 0),
        level: num(v["level"] ?? 0, 0),
      });
    case "priceCheap":
    case "priceDear":
      return t(`automations.reason.${d.reason}`, {
        price: num(v["price"] ?? 0, 0),
        limit: num(v["limit"] ?? 0, 0),
        hours: v["hours"] ?? 0,
      });
    case "priceOutside":
    case "forecastOutside":
      return t(`automations.reason.${d.reason}`);
    case "forecastLow":
    case "forecastEnough":
      return t(`automations.reason.${d.reason}`, {
        kwh: num(v["forecastKwh"] ?? 0, 1),
        min: num(v["minKwh"] ?? 0, 1),
      });
    default:
      return d.reason;
  }
}

/** What a rule watches, in a line: its window, its level, or its surplus thresholds. */
function ruleText(r: AutomationRule): string {
  if (r.kind === "schedule" && r.schedule) {
    return t("automations.summary.schedule", {
      days: r.schedule.days.map((d) => t(`automations.day.${d}`)).join(", "),
      on: r.schedule.on,
      off: r.schedule.off,
    });
  }
  if (r.kind === "threshold" && r.threshold) {
    const point = siteTree()?.points.find((p) => p.id === r.threshold!.pointId);
    return t("automations.summary.threshold", {
      point: point?.name ?? r.threshold.pointId,
      comparison: t(`automations.comparison.${r.threshold.comparison}`),
      value: num(r.threshold.value, 2),
      minutes: r.threshold.minutes,
      action: t(`automations.action.${r.threshold.action}`),
    });
  }
  if (r.kind === "pvSurplus" && r.surplus) {
    return t("automations.summary.pvSurplus", {
      on: num(r.surplus.onExportKw),
      off: num(r.surplus.offImportKw),
      minutes: r.surplus.minutes,
      min: r.surplus.minOnMinutes,
    });
  }
  if (r.kind === "price" && r.price) {
    return r.price.mode === "cheapest"
      ? t("automations.summary.priceCheapest", {
          hours: r.price.hours,
          start: r.price.start,
          end: r.price.end,
        })
      : t("automations.summary.priceLevel", {
          comparison: t(`automations.comparison.${r.price.comparison}`),
          price: num(r.price.pricePlnMwh, 0),
          action: t(`automations.action.${r.price.action}`),
        });
  }
  if (r.kind === "pvForecast" && r.forecast) {
    return t("automations.summary.pvForecast", {
      on: r.forecast.on,
      off: r.forecast.off,
      min: num(r.forecast.minKwh, 1),
      kwp: num(r.forecast.kwp, 1),
    });
  }
  return "";
}

/**
 * Automations (prototype "Automatyzacje", "Nadwyżka z PV"), in test mode: rules that switch a device by a schedule,
 * by a value held for some minutes, or by PV surplus, and the log of what they would have done. Nothing is sent to
 * devices yet; the log shows the switch's state at each decision, so the rules can be checked before they act.
 */
export function AutomationsPage() {
  const [rules, { refetch: refetchRules }] = createResource(() =>
    api.automations.list(),
  );
  const [targets, { refetch: refetchTargets }] = createResource(() =>
    api.automations.targets(),
  );
  const [decisions, { refetch: refetchDecisions }] = createResource(() =>
    api.automations.decisions(),
  );
  const [editing, setEditing] = createSignal<{ rule?: AutomationRule }>();
  const [removing, setRemoving] = createSignal<AutomationRule>();
  const reload = () => {
    api.invalidate("/api/v1/automations");
    void refetchRules();
    void refetchDecisions();
  };
  const targetName = (r: { target: { deviceId: string; channel: string } }) => {
    const x = (targets() ?? []).find(
      (s) => s.deviceId === r.target.deviceId && s.channel === r.target.channel,
    );
    return x ? `${x.deviceName} · ${x.channel}` : r.target.deviceId;
  };
  const ruleOf = (id: string) => (rules() ?? []).find((r) => r.id === id);

  return (
    <>
      <PageHeader
        eyebrow={t("automations.eyebrow")}
        title={t("automations.title")}
        sub={t("automations.sub")}
      >
        <Show when={operator()}>
          <button
            type="button"
            class="btn primary"
            onClick={() => setEditing({})}
          >
            <Icon name="plus" />
            {t("automations.new")}
          </button>
        </Show>
      </PageHeader>
      <div class="note test-mode">
        <Icon name="alert" />
        <span>
          {t(
            (rules() ?? []).some((r) => r.enabled && r.mode === "live")
              ? "automations.liveNote"
              : "automations.testMode",
          )}
        </span>
      </div>
      <div class="grid g-2">
        <section class="card">
          <div class="card-h">
            <h2>{t("automations.rules")}</h2>
          </div>
          <Show when={!rules.error} fallback={<ErrorBox error={rules.error} />}>
            <Show when={rules()} fallback={<Skeleton lines={4} />}>
              {(list) => (
                <Show
                  when={list().length > 0}
                  fallback={
                    <div class="card-b hint">{t("automations.none")}</div>
                  }
                >
                  <table class="tbl">
                    <tbody>
                      <For each={list()}>
                        {(r) => (
                          <tr>
                            <td>
                              <b style={{ "font-weight": 500 }}>{r.name}</b>
                              <div class="hint">{ruleText(r)}</div>
                              <div class="hint">
                                {targetName(r)} · {placeName(r.nodeId)}
                              </div>
                            </td>
                            <td>
                              <span
                                class={`chip ${!r.enabled ? "" : r.mode === "live" ? "ok" : "acc"}`}
                              >
                                {r.enabled
                                  ? t(`automations.mode.${r.mode ?? "test"}`)
                                  : t("automations.off")}
                              </span>
                            </td>
                            <td class="r">
                              <Show when={operator()}>
                                <button
                                  type="button"
                                  class="btn ghost sm"
                                  title={t("common.edit")}
                                  aria-label={t("common.edit")}
                                  onClick={() => setEditing({ rule: r })}
                                >
                                  <Icon name="edit" />
                                </button>
                                <button
                                  type="button"
                                  class="btn ghost sm"
                                  title={t("common.delete")}
                                  aria-label={t("common.delete")}
                                  onClick={() => setRemoving(r)}
                                >
                                  <Icon name="x" />
                                </button>
                              </Show>
                            </td>
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                </Show>
              )}
            </Show>
          </Show>
        </section>
        <section class="card">
          <div class="card-h">
            <h2>{t("automations.log")}</h2>
            <span class="meta">{t("automations.logMeta")}</span>
          </div>
          <Show
            when={!decisions.error}
            fallback={<ErrorBox error={decisions.error} />}
          >
            <Show
              when={(decisions() ?? []).length > 0}
              fallback={
                <div class="card-b hint">
                  {decisions.loading ? "…" : t("automations.noDecisions")}
                </div>
              }
            >
              <div class="scrollx">
                <table class="tbl">
                  <tbody>
                    <For each={decisions()}>
                      {(d) => (
                        <tr>
                          <td
                            class="num hint"
                            style={{ "white-space": "nowrap" }}
                          >
                            {formatDateTime(d.at, locale())}
                          </td>
                          <td>
                            <b style={{ "font-weight": 500 }}>
                              {ruleOf(d.ruleId)?.name ?? d.ruleId}
                            </b>
                            <div class="hint">
                              {reasonText(d, ruleOf(d.ruleId))}
                            </div>
                          </td>
                          <td>
                            <span
                              class={`chip ${d.action === "on" ? "ok" : ""}`}
                            >
                              {t(`automations.action.${d.action}`)}
                            </span>
                          </td>
                          <td>
                            <span
                              class={`chip ${d.outcome === "test" ? "acc" : d.outcome === "sent" ? "ok" : d.outcome === "failed" ? "crit" : ""}`}
                              title={t("automations.stateThen", {
                                state: d.switchState
                                  ? t(`automations.state.${d.switchState}`)
                                  : t("automations.state.unknown"),
                              })}
                            >
                              {t(`automations.outcome.${d.outcome}`)}
                            </span>
                          </td>
                        </tr>
                      )}
                    </For>
                  </tbody>
                </table>
              </div>
            </Show>
          </Show>
        </section>
      </div>
      <Switches
        targets={targets() ?? []}
        onSwitched={() => void refetchTargets()}
      />
      <Show when={editing()}>
        {(e) => (
          <RuleDialog
            rule={e().rule}
            targets={targets() ?? []}
            onClose={() => setEditing(undefined)}
            onSaved={reload}
          />
        )}
      </Show>
      <Show when={removing()}>
        {(r) => (
          <ConfirmDialog
            title={t("automations.removeTitle", { name: r().name })}
            text={t("automations.removeText")}
            confirm={t("common.delete")}
            danger
            onConfirm={async () => {
              await api.automations.remove(r().id);
              reload();
            }}
            onClose={() => setRemoving(undefined)}
          />
        )}
      </Show>
    </>
  );
}

const numOrNaN = (s: string) => {
  const v = Number(s.replace(",", ".").trim());
  return s.trim() === "" ? NaN : v;
};

/** A rule to create or change: what it watches, where, and which switch it would control. */
function RuleDialog(props: {
  rule?: AutomationRule;
  targets: SwitchTarget[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const r = props.rule;
  const places = () => treeRows(siteTree()?.nodes ?? []);
  const [name, setName] = createSignal(r?.name ?? "");
  const [kind, setKind] = createSignal<Kind>((r?.kind as Kind) ?? "pvSurplus");
  const [node, setNode] = createSignal(r?.nodeId ?? places()[0]?.node.id ?? "");
  const [target, setTarget] = createSignal(
    r
      ? `${r.target.deviceId}|${r.target.channel}`
      : props.targets[0]
        ? `${props.targets[0].deviceId}|${props.targets[0].channel}`
        : "",
  );
  const [enabled, setEnabled] = createSignal(r?.enabled ?? true);
  const [mode, setMode] = createSignal<"test" | "live">(r?.mode ?? "test");
  const [priceMode, setPriceMode] = createSignal<"cheapest" | "level">(
    r?.price?.mode ?? "cheapest",
  );
  const [cheapHours, setCheapHours] = createSignal(
    String(r?.price?.hours ?? 3),
  );
  const [priceStart, setPriceStart] = createSignal(r?.price?.start ?? "22:00");
  const [priceEnd, setPriceEnd] = createSignal(r?.price?.end ?? "06:00");
  const [priceCmp, setPriceCmp] = createSignal(r?.price?.comparison ?? "above");
  const [priceLevel, setPriceLevel] = createSignal(
    String(r?.price?.pricePlnMwh ?? 1000),
  );
  const [priceAction, setPriceAction] = createSignal(r?.price?.action ?? "off");
  const [kwp, setKwp] = createSignal(String(r?.forecast?.kwp ?? 5));
  const [minKwh, setMinKwh] = createSignal(String(r?.forecast?.minKwh ?? 8));
  const [fcOn, setFcOn] = createSignal(r?.forecast?.on ?? "22:00");
  const [fcOff, setFcOff] = createSignal(r?.forecast?.off ?? "06:00");
  const [days, setDays] = createSignal<number[]>(
    r?.schedule?.days ?? [1, 2, 3, 4, 5],
  );
  const [on, setOn] = createSignal(r?.schedule?.on ?? "22:00");
  const [off, setOff] = createSignal(r?.schedule?.off ?? "06:00");
  const [point, setPoint] = createSignal(r?.threshold?.pointId ?? "");
  const [comparison, setComparison] = createSignal(
    r?.threshold?.comparison ?? "below",
  );
  const [level, setLevel] = createSignal(String(r?.threshold?.value ?? ""));
  const [minutes, setMinutes] = createSignal(
    String(r?.threshold?.minutes ?? r?.surplus?.minutes ?? 15),
  );
  const [action, setAction] = createSignal(r?.threshold?.action ?? "on");
  const [revert, setRevert] = createSignal(r?.threshold?.revert ?? true);
  const [exportKw, setExportKw] = createSignal(
    String(r?.surplus?.onExportKw ?? 1.5),
  );
  const [importKw, setImportKw] = createSignal(
    String(r?.surplus?.offImportKw ?? 0.3),
  );
  const [minOn, setMinOn] = createSignal(
    String(r?.surplus?.minOnMinutes ?? 30),
  );
  const [run, error, busy] = useAction();
  const points = () =>
    (siteTree()?.points ?? [])
      .filter((p) => p.binding)
      .sort((a, b) => a.name.localeCompare(b.name));

  const input = (): AutomationInput => {
    const [deviceId, channel] = target().split("|");
    return {
      name: name().trim(),
      kind: kind(),
      nodeId: node(),
      target: { deviceId: deviceId ?? "", channel: channel ?? "" },
      enabled: enabled(),
      mode: mode(),
      price:
        kind() === "price"
          ? {
              mode: priceMode(),
              hours: Math.round(numOrNaN(cheapHours())),
              start: priceStart(),
              end: priceEnd(),
              comparison: priceCmp(),
              pricePlnMwh: numOrNaN(priceLevel()),
              action: priceAction(),
            }
          : null,
      forecast:
        kind() === "pvForecast"
          ? {
              kwp: numOrNaN(kwp()),
              minKwh: numOrNaN(minKwh()),
              on: fcOn(),
              off: fcOff(),
            }
          : null,
      schedule:
        kind() === "schedule" ? { days: days(), on: on(), off: off() } : null,
      threshold:
        kind() === "threshold"
          ? {
              pointId: point(),
              comparison: comparison(),
              value: numOrNaN(level()),
              minutes: Math.round(numOrNaN(minutes())),
              action: action(),
              revert: revert(),
            }
          : null,
      surplus:
        kind() === "pvSurplus"
          ? {
              onExportKw: numOrNaN(exportKw()),
              offImportKw: numOrNaN(importKw()),
              minutes: Math.round(numOrNaN(minutes())),
              minOnMinutes: Math.round(numOrNaN(minOn())),
            }
          : null,
    };
  };

  return (
    <Dialog
      title={
        r ? t("automations.editTitle", { name: r.name }) : t("automations.new")
      }
      wide
      onClose={props.onClose}
      footer={
        <>
          <button type="button" class="btn" onClick={() => props.onClose()}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            class="btn primary"
            disabled={busy() || !name().trim() || !target() || !node()}
            onClick={() =>
              void run(async () => {
                await api.automations.save(r?.id ?? null, input());
                props.onSaved();
                props.onClose();
              })
            }
          >
            {t("common.save")}
          </button>
        </>
      }
    >
      <div style={{ display: "grid", gap: "10px" }}>
        <Show when={props.targets.length === 0}>
          <p class="chip warn" style={{ margin: 0, "justify-self": "start" }}>
            {t("automations.noTargets")}
          </p>
        </Show>
        <div class="field">
          <label for="auName">{t("automations.name")}</label>
          <input
            id="auName"
            value={name()}
            placeholder={t("automations.namePlaceholder")}
            onInput={(e) => setName(e.currentTarget.value)}
          />
        </div>
        <div class="seg" role="group" aria-label={t("automations.kind")}>
          <For each={KINDS}>
            {(k) => (
              <button
                type="button"
                aria-pressed={kind() === k}
                onClick={() => setKind(k)}
              >
                {t(`automations.kinds.${k}`)}
              </button>
            )}
          </For>
        </div>
        <p class="hint" style={{ margin: 0 }}>
          {t(`automations.kindHint.${kind()}`)}
        </p>
        <div class="grid g-2e" style={{ gap: "10px" }}>
          <div class="field">
            <label for="auTarget">{t("automations.target")}</label>
            <select
              id="auTarget"
              value={target()}
              onChange={(e) => setTarget(e.currentTarget.value)}
            >
              <For each={props.targets}>
                {(x) => (
                  <option value={`${x.deviceId}|${x.channel}`}>
                    {x.deviceName} · {x.channel}
                    {x.state ? ` (${t(`automations.state.${x.state}`)})` : ""}
                  </option>
                )}
              </For>
            </select>
          </div>
          <div class="field">
            <label for="auNode">{t("automations.place")}</label>
            <select
              id="auNode"
              value={node()}
              onChange={(e) => setNode(e.currentTarget.value)}
            >
              <For each={places()}>
                {(row) => (
                  <option value={row.node.id}>
                    {"  ".repeat(row.depth)}
                    {row.node.name}
                  </option>
                )}
              </For>
            </select>
            <span class="hint">{t(`automations.placeHint.${kind()}`)}</span>
          </div>
        </div>

        <Show when={kind() === "schedule"}>
          <div class="field">
            <span class="eyebrow">{t("automations.days")}</span>
            <div style={{ display: "flex", gap: "10px", "flex-wrap": "wrap" }}>
              <For each={DAYS}>
                {(d) => (
                  <label
                    style={{
                      display: "inline-flex",
                      gap: "4px",
                      "align-items": "center",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={days().includes(d)}
                      onChange={(e) =>
                        setDays(
                          e.currentTarget.checked
                            ? [...days(), d].sort()
                            : days().filter((x) => x !== d),
                        )
                      }
                    />
                    {t(`automations.day.${d}`)}
                  </label>
                )}
              </For>
            </div>
          </div>
          <div class="grid g-2e" style={{ gap: "10px" }}>
            <div class="field">
              <label for="auOn">{t("automations.onAt")}</label>
              <input
                id="auOn"
                type="time"
                value={on()}
                onInput={(e) => setOn(e.currentTarget.value)}
              />
            </div>
            <div class="field">
              <label for="auOff">{t("automations.offAt")}</label>
              <input
                id="auOff"
                type="time"
                value={off()}
                onInput={(e) => setOff(e.currentTarget.value)}
              />
            </div>
          </div>
        </Show>

        <Show when={kind() === "threshold"}>
          <div class="field">
            <label for="auPoint">{t("automations.point")}</label>
            <select
              id="auPoint"
              value={point()}
              onChange={(e) => setPoint(e.currentTarget.value)}
            >
              <option value="">—</option>
              <For each={points()}>
                {(p) => (
                  <option value={p.id}>
                    {p.name} · {placeName(p.nodeId)}
                  </option>
                )}
              </For>
            </select>
          </div>
          <div class="grid g-2e" style={{ gap: "10px" }}>
            <div class="field">
              <label for="auCmp">{t("automations.when")}</label>
              <select
                id="auCmp"
                value={comparison()}
                onChange={(e) => setComparison(e.currentTarget.value)}
              >
                <option value="below">
                  {t("automations.comparison.below")}
                </option>
                <option value="above">
                  {t("automations.comparison.above")}
                </option>
              </select>
            </div>
            <div class="field">
              <label for="auLevel">{t("automations.level")}</label>
              <input
                id="auLevel"
                inputmode="decimal"
                value={level()}
                onInput={(e) => setLevel(e.currentTarget.value)}
              />
            </div>
            <div class="field">
              <label for="auMin">{t("automations.minutes")}</label>
              <input
                id="auMin"
                inputmode="numeric"
                value={minutes()}
                onInput={(e) => setMinutes(e.currentTarget.value)}
              />
            </div>
            <div class="field">
              <label for="auAct">{t("automations.then")}</label>
              <select
                id="auAct"
                value={action()}
                onChange={(e) => setAction(e.currentTarget.value)}
              >
                <option value="on">{t("automations.action.on")}</option>
                <option value="off">{t("automations.action.off")}</option>
              </select>
            </div>
          </div>
          <label class="opt">
            <input
              type="checkbox"
              checked={revert()}
              onChange={(e) => setRevert(e.currentTarget.checked)}
            />
            <div>
              <b>{t("automations.revert")}</b>
              <small>{t("automations.revertHint")}</small>
            </div>
          </label>
        </Show>

        <Show when={kind() === "pvSurplus"}>
          <div class="grid g-2e" style={{ gap: "10px" }}>
            <div class="field">
              <label for="auExp">{t("automations.exportKw")}</label>
              <input
                id="auExp"
                inputmode="decimal"
                value={exportKw()}
                onInput={(e) => setExportKw(e.currentTarget.value)}
              />
            </div>
            <div class="field">
              <label for="auImp">{t("automations.importKw")}</label>
              <input
                id="auImp"
                inputmode="decimal"
                value={importKw()}
                onInput={(e) => setImportKw(e.currentTarget.value)}
              />
            </div>
            <div class="field">
              <label for="auMin2">{t("automations.minutes")}</label>
              <input
                id="auMin2"
                inputmode="numeric"
                value={minutes()}
                onInput={(e) => setMinutes(e.currentTarget.value)}
              />
            </div>
            <div class="field">
              <label for="auMinOn">{t("automations.minOn")}</label>
              <input
                id="auMinOn"
                inputmode="numeric"
                value={minOn()}
                onInput={(e) => setMinOn(e.currentTarget.value)}
              />
            </div>
          </div>
        </Show>

        <Show when={kind() === "price"}>
          <div class="seg" role="group" aria-label={t("automations.priceMode")}>
            <For each={["cheapest", "level"] as const}>
              {(m) => (
                <button
                  type="button"
                  aria-pressed={priceMode() === m}
                  onClick={() => setPriceMode(m)}
                >
                  {t(`automations.priceModes.${m}`)}
                </button>
              )}
            </For>
          </div>
          <Show
            when={priceMode() === "cheapest"}
            fallback={
              <div class="grid g-2e" style={{ gap: "10px" }}>
                <div class="field">
                  <label for="auPCmp">{t("automations.when")}</label>
                  <select
                    id="auPCmp"
                    value={priceCmp()}
                    onChange={(e) => setPriceCmp(e.currentTarget.value)}
                  >
                    <option value="above">
                      {t("automations.comparison.above")}
                    </option>
                    <option value="below">
                      {t("automations.comparison.below")}
                    </option>
                  </select>
                </div>
                <div class="field">
                  <label for="auPLevel">{t("automations.priceLevel")}</label>
                  <input
                    id="auPLevel"
                    inputmode="decimal"
                    value={priceLevel()}
                    onInput={(e) => setPriceLevel(e.currentTarget.value)}
                  />
                </div>
                <div class="field">
                  <label for="auPAct">{t("automations.then")}</label>
                  <select
                    id="auPAct"
                    value={priceAction()}
                    onChange={(e) => setPriceAction(e.currentTarget.value)}
                  >
                    <option value="off">{t("automations.action.off")}</option>
                    <option value="on">{t("automations.action.on")}</option>
                  </select>
                </div>
              </div>
            }
          >
            <div class="grid g-2e" style={{ gap: "10px" }}>
              <div class="field">
                <label for="auPHours">{t("automations.cheapHours")}</label>
                <input
                  id="auPHours"
                  inputmode="numeric"
                  value={cheapHours()}
                  onInput={(e) => setCheapHours(e.currentTarget.value)}
                />
              </div>
              <div class="field">
                <label for="auPStart">{t("automations.windowStart")}</label>
                <input
                  id="auPStart"
                  type="time"
                  value={priceStart()}
                  onInput={(e) => setPriceStart(e.currentTarget.value)}
                />
              </div>
              <div class="field">
                <label for="auPEnd">{t("automations.windowEnd")}</label>
                <input
                  id="auPEnd"
                  type="time"
                  value={priceEnd()}
                  onInput={(e) => setPriceEnd(e.currentTarget.value)}
                />
              </div>
            </div>
          </Show>
        </Show>

        <Show when={kind() === "pvForecast"}>
          <div class="grid g-2e" style={{ gap: "10px" }}>
            <div class="field">
              <label for="auKwp">{t("automations.kwp")}</label>
              <input
                id="auKwp"
                inputmode="decimal"
                value={kwp()}
                onInput={(e) => setKwp(e.currentTarget.value)}
              />
            </div>
            <div class="field">
              <label for="auMinKwh">{t("automations.minKwh")}</label>
              <input
                id="auMinKwh"
                inputmode="decimal"
                value={minKwh()}
                onInput={(e) => setMinKwh(e.currentTarget.value)}
              />
            </div>
            <div class="field">
              <label for="auFcOn">{t("automations.windowStart")}</label>
              <input
                id="auFcOn"
                type="time"
                value={fcOn()}
                onInput={(e) => setFcOn(e.currentTarget.value)}
              />
            </div>
            <div class="field">
              <label for="auFcOff">{t("automations.windowEnd")}</label>
              <input
                id="auFcOff"
                type="time"
                value={fcOff()}
                onInput={(e) => setFcOff(e.currentTarget.value)}
              />
            </div>
          </div>
        </Show>

        <div class="field">
          <span class="eyebrow">{t("automations.modeLabel")}</span>
          <div class="seg" role="group" aria-label={t("automations.modeLabel")}>
            <For each={["test", "live"] as const}>
              {(m) => (
                <button
                  type="button"
                  aria-pressed={mode() === m}
                  onClick={() => setMode(m)}
                >
                  {t(`automations.mode.${m}`)}
                </button>
              )}
            </For>
          </div>
          <span class={mode() === "live" ? "chip warn" : "hint"}>
            {t(`automations.modeHint.${mode()}`)}
          </span>
        </div>
        <label class="opt">
          <input
            type="checkbox"
            checked={enabled()}
            onChange={(e) => setEnabled(e.currentTarget.checked)}
          />
          <div>
            <b>{t("automations.enabled")}</b>
            <small>{t("automations.enabledHint")}</small>
          </div>
        </label>
        <Show when={error()}>
          <ErrorBox error={error()} />
        </Show>
      </div>
    </Dialog>
  );
}

/**
 * The switches automations can control, with their state now, turned on and off by hand: a real command to the
 * device through its integration (Exalus, SUPLA). The state shown changes once the device reports it.
 */
function Switches(props: { targets: SwitchTarget[]; onSwitched: () => void }) {
  const [result, setResult] = createSignal<{
    key: string;
    ok: boolean;
    code: string | null;
  }>();
  const [busy, setBusy] = createSignal<string>();
  const send = async (x: SwitchTarget, on: boolean) => {
    const key = `${x.deviceId}|${x.channel}`;
    setBusy(key);
    try {
      const r = await api.automations.switchNow(x.deviceId, x.channel, on);
      setResult({ key, ok: r.sent, code: r.errorCode });
      // The new state arrives with the device's next reading.
      setTimeout(props.onSwitched, 2500);
    } catch {
      setResult({ key, ok: false, code: null });
    } finally {
      setBusy(undefined);
    }
  };
  return (
    <Show when={props.targets.length > 0}>
      <section class="card">
        <div class="card-h">
          <h2>{t("automations.switches")}</h2>
          <span class="meta">{t("automations.switchesMeta")}</span>
        </div>
        <div class="scrollx">
          <table class="tbl">
            <tbody>
              <For each={props.targets}>
                {(x) => {
                  const key = `${x.deviceId}|${x.channel}`;
                  return (
                    <tr>
                      <td>
                        <b style={{ "font-weight": 500 }}>{x.deviceName}</b>
                        <div class="hint">{x.channel}</div>
                      </td>
                      <td>
                        <span
                          class={`chip ${x.state === "on" ? "ok" : ""}`}
                          title={x.at ? formatDateTime(x.at, locale()) : ""}
                        >
                          {t(`automations.state.${x.state ?? "unknown"}`)}
                        </span>
                      </td>
                      <td>
                        <Show when={result()?.key === key}>
                          <span class={`chip ${result()!.ok ? "ok" : "crit"}`}>
                            {result()!.ok
                              ? t("automations.switchSent")
                              : t("automations.switchFailed", {
                                  code: result()!.code ?? "",
                                })}
                          </span>
                        </Show>
                      </td>
                      <td class="r" style={{ "white-space": "nowrap" }}>
                        <Show when={operator()}>
                          <button
                            type="button"
                            class="btn sm"
                            disabled={busy() === key}
                            onClick={() => void send(x, true)}
                          >
                            {t("automations.action.on")}
                          </button>{" "}
                          <button
                            type="button"
                            class="btn sm"
                            disabled={busy() === key}
                            onClick={() => void send(x, false)}
                          >
                            {t("automations.action.off")}
                          </button>
                        </Show>
                      </td>
                    </tr>
                  );
                }}
              </For>
            </tbody>
          </table>
        </div>
        <div class="card-b hint">{t("automations.switchesHint")}</div>
      </section>
    </Show>
  );
}
